import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  LuBriefcase,
  LuChevronDown,
  LuChevronRight,
  LuDownload,
  LuEye,
  LuEyeOff,
  LuFolderInput,
  LuFolderPlus,
  LuInbox,
  LuLink,
  LuPencil,
  LuPenLine,
  LuSearch,
  LuTrash2,
  LuUpload,
  LuX,
} from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  useGetClientPortalQuery,
  useShareWithClientMutation,
} from "../../slices/ClientPortalSlice";
import {
  downloadZip,
  useDeleteFilesMutation,
  useDeleteFolderMutation,
  useGetClientFilesQuery,
  useMoveFilesMutation,
  useUpdateFolderMutation,
} from "../../slices/DocumentsSlice";
import { isPdfName, signatureRequestPath } from "../signatures/signature.utils";
import {
  DeleteFilesModal,
  FolderFormModal,
  LinkWorkModal,
  RenameFileModal,
  UploadFilesModal,
} from "./FileDialogs";
import { FileItem, FileRef, FolderItem } from "./files.types";
import {
  buildTree,
  canMoveFolder,
  canShareDirectly,
  countInFolder,
  FileFilters,
  filesInView,
  FileSort,
  FilesView,
  filterFiles,
  folderChain,
  FolderNode,
  folderSubtree,
  hasFilters,
  refKey,
  sortFiles,
  toRef,
} from "./files.utils";
import { FileTable } from "./FileTable";
import { FolderPickerModal } from "./FolderPicker";
import {
  ErrorText,
  errorText,
  FolderGlyph,
  GHOST_BUTTON,
  INPUT,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  SELECT,
  VisibilityPill,
} from "./ui/FilesUi";
import { useFileOpener } from "./useFileOpener";

const DRAG_TYPE = "application/x-procharted-files";

type Dialog =
  | { type: "upload"; files?: File[] }
  | { type: "newFolder"; parentId: string | null }
  | { type: "editFolder"; folder: FolderItem }
  | { type: "moveFolder"; folder: FolderItem }
  | { type: "deleteFolder"; folder: FolderItem }
  | { type: "move"; files: FileItem[] }
  | { type: "link"; files: FileItem[] }
  | { type: "rename"; file: FileItem }
  | { type: "delete"; files: FileItem[] }
  | null;

/** A client's Files tab: folder tree on the left, the open folder's files on the right. */
export const ClientFilesTab: FC<{ customerId: string }> = ({ customerId }) => {
  const {
    data,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useGetClientFilesQuery(customerId, {
    skip: !customerId,
    refetchOnMountOrArgChange: true,
  });
  const { data: portal } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const portalActive = !!portal?.people?.some((p) => p.status === "active");
  const navigate = useNavigate();

  const [view, setView] = useState<FilesView>({ type: "top" });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FileFilters>({});
  const [sort, setSort] = useState<FileSort>("newest");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [clientView, setClientView] = useState(false);

  const [moveFiles, { isLoading: moving }] = useMoveFilesMutation();
  const [deleteFiles, { isLoading: deleting }] = useDeleteFilesMutation();
  const [deleteFolder, { isLoading: deletingFolder }] =
    useDeleteFolderMutation();
  const [updateFolder, { isLoading: movingFolder }] = useUpdateFolderMutation();
  const [share] = useShareWithClientMutation();
  const { openFile, downloadFile, preview } = useFileOpener(setError);

  const folders = useMemo(() => data?.folders || [], [data]);
  const files = useMemo(() => data?.files || [], [data]);
  const tree = useMemo(() => buildTree(folders), [folders]);
  const toSortCount = useMemo(
    () => filesInView(files, { type: "toSort" }).length,
    [files],
  );
  const worksWithFiles = useMemo(
    () =>
      (data?.works || [])
        .map((w) => ({
          ...w,
          count: files.filter((f) => f.workId === w.id).length,
        }))
        .filter((w) => w.count > 0),
    [data, files],
  );

  // Roadmap 1878 (owner 2026-10-22): the folder list starts collapsed. Opening
  // a folder (openFolder) expands it and its parents; what the person opens or
  // closes is kept while they stay on the tab.
  useEffect(() => {
    if (view.type === "folder" && !folders.some((f) => f.id === view.id))
      setView({ type: "top" });
  }, [folders, view]);
  useEffect(() => setSelected(new Set()), [view]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const filtering = hasFilters(filters) || clientView;
  const effectiveFilters: FileFilters = clientView
    ? { ...filters, clientCanSee: "yes" }
    : filters;
  const shownFiles = useMemo(() => {
    const base = filtering
      ? files.filter((f) => f.kind === "client" || f.workId)
      : filesInView(files, view);
    return sortFiles(filterFiles(base, effectiveFilters), sort);
  }, [files, view, filters, sort, clientView]);
  const shownFolders = useMemo(() => {
    if (filtering || (view.type !== "top" && view.type !== "folder")) return [];
    const parentId = view.type === "folder" ? view.id : null;
    return folders
      .filter((f) => (f.parentId || null) === parentId)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      );
  }, [folders, view, filtering]);

  const selectedFiles = files.filter((f) => selected.has(refKey(toRef(f))));
  const currentFolder =
    view.type === "folder"
      ? folders.find((f) => f.id === view.id) || null
      : null;
  const chain = folderChain(folders, currentFolder?.id || null);
  const currentWork =
    view.type === "work" ? data?.works.find((w) => w.id === view.id) : null;
  const uploadFolderId = view.type === "folder" ? view.id : null;

  const keysToFiles = (keys: string[]) =>
    files.filter((f) => keys.includes(refKey(toRef(f))));

  const move = async (items: FileRef[], folderId: string | null) => {
    setError("");
    try {
      const result = await moveFiles({ items, folderId }).unwrap();
      const target = folderId
        ? folders.find((f) => f.id === folderId)?.name
        : "the top level";
      setNotice(
        `Moved ${result.count} file${
          result.count === 1 ? "" : "s"
        } to ${target}.`,
      );
      setSelected(new Set());
      setDialog(null);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const remove = async (list: FileItem[]) => {
    setError("");
    try {
      await deleteFiles(list.map(toRef)).unwrap();
      setNotice(
        `${list.length} file${
          list.length === 1 ? "" : "s"
        } moved to the recycle bin.`,
      );
      setSelected(new Set());
      setDialog(null);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const toggleShare = async (list: FileItem[], shared: boolean) => {
    const ids = list.filter(canShareDirectly).map((f) => f.id);
    if (ids.length === 0) {
      setError(
        "Only files your team added can be shared one by one. Client uploads are always in their portal.",
      );
      return;
    }
    setError("");
    try {
      await share({ documentIds: ids, shared }).unwrap();
      setNotice(
        shared
          ? "Shared with the client."
          : "No longer shared with the client.",
      );
      refetch();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const zip = async (items: FileRef[], folderIds: string[] = []) => {
    setError("");
    setNotice("Preparing the download…");
    try {
      await downloadZip(items, folderIds);
      setNotice("");
    } catch (e) {
      setNotice("");
      setError(errorText(e));
    }
  };

  const fileMenu = (file: FileItem) => {
    const items = [
      {
        label: "Preview",
        icon: <LuEye size={15} />,
        onClick: () => openFile(file),
      },
      {
        label: "Download",
        icon: <LuDownload size={15} />,
        onClick: () => downloadFile(file),
      },
      {
        label: "Rename",
        icon: <LuPencil size={15} />,
        onClick: () => setDialog({ type: "rename", file }),
      },
      {
        label: "Move to…",
        icon: <LuFolderInput size={15} />,
        onClick: () => setDialog({ type: "move", files: [file] }),
      },
      {
        label: "Work item…",
        icon: <LuLink size={15} />,
        onClick: () => setDialog({ type: "link", files: [file] }),
      },
    ];
    // Owner 2026-10-28: "Request signature" for the client's PDFs (not older work-item files).
    if (file.kind === "client" && isPdfName(file.name) && !portal?.archived)
      items.push({
        label: "Request signature",
        icon: <LuPenLine size={15} />,
        onClick: () =>
          navigate(signatureRequestPath(customerId, file.id, file.name)),
      });
    if (canShareDirectly(file)) {
      items.push(
        file.sharedDirectly
          ? {
              label: "Stop sharing",
              icon: <LuEyeOff size={15} />,
              onClick: () => toggleShare([file], false),
            }
          : {
              label: "Share",
              icon: <LuEye size={15} />,
              onClick: () => toggleShare([file], true),
            },
      );
    }
    return [
      ...items,
      {
        label: "Delete",
        icon: <LuTrash2 size={15} />,
        danger: true,
        onClick: () => setDialog({ type: "delete", files: [file] }),
      },
    ];
  };

  const folderMenu = (folder: FolderItem) => [
    {
      label: "Open",
      icon: <FolderGlyph size={15} />,
      onClick: () => openFolder(folder.id),
    },
    {
      label: "New folder",
      icon: <LuFolderPlus size={15} />,
      onClick: () => setDialog({ type: "newFolder", parentId: folder.id }),
    },
    {
      label: "Edit",
      icon: <LuPencil size={15} />,
      onClick: () => setDialog({ type: "editFolder", folder }),
    },
    {
      label: "Move to…",
      icon: <LuFolderInput size={15} />,
      onClick: () => setDialog({ type: "moveFolder", folder }),
    },
    {
      label: "Download",
      icon: <LuDownload size={15} />,
      onClick: () => zip([], [folder.id]),
    },
    {
      label: "Delete",
      icon: <LuTrash2 size={15} />,
      danger: true,
      onClick: () => setDialog({ type: "deleteFolder", folder }),
    },
  ];

  const openFolder = (id: string) => {
    setFilters({});
    setClientView(false);
    setView({ type: "folder", id });
    setOpen((current) => {
      const next = new Set(current);
      folderChain(folders, id).forEach((f) => next.add(f.id));
      return next;
    });
  };

  const onDropOnFolder = (event: React.DragEvent, folderId: string | null) => {
    event.preventDefault();
    setDropTarget(null);
    const raw = event.dataTransfer.getData(DRAG_TYPE);
    if (raw) {
      const list = keysToFiles(JSON.parse(raw) as string[]);
      if (list.length) move(list.map(toRef), folderId);
      return;
    }
    if (event.dataTransfer.files?.length) {
      if (folderId) openFolder(folderId);
      setDialog({
        type: "upload",
        files: Array.from(event.dataTransfer.files),
      });
    }
  };

  const dropProps = (folderId: string | null, key: string) => ({
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      setDropTarget(key);
    },
    onDragLeave: () =>
      setDropTarget((current) => (current === key ? null : current)),
    onDrop: (event: React.DragEvent) => onDropOnFolder(event, folderId),
  });

  if (isLoading) return <LottieLoading />;
  if (isError || !data) {
    return (
      <div
        className={
          "rounded-2xl border border-[#E6E9EF] bg-white p-8 text-center text-[#4A5168]"
        }>
        {errorText(loadError, "The files couldn't be loaded.")}
        <div className={"mt-3"}>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={() => refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const renderNode = (node: FolderNode, depth: number): React.ReactNode => {
    const isOpen = open.has(node.id);
    const active = view.type === "folder" && view.id === node.id;
    const dropKey = `folder:${node.id}`;
    return (
      <React.Fragment key={node.id}>
        <div
          {...dropProps(node.id, dropKey)}
          className={classNames(
            "group flex items-center gap-1 rounded-lg py-1.5 pr-1.5 text-[13px]",
            active
              ? "bg-[#EFEBFF] font-bold text-[#4B32D6]"
              : "font-semibold text-[#1F2433] hover:bg-white",
            dropTarget === dropKey && "ring-2 ring-[#6B4EFF]",
          )}
          style={{ paddingLeft: 4 + depth * 16 }}>
          <button
            type={"button"}
            aria-label={isOpen ? `Close ${node.name}` : `Open ${node.name}`}
            onClick={() =>
              setOpen((current) => {
                const next = new Set(current);
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
                return next;
              })
            }
            className={classNames(
              "flex h-5 w-5 flex-none items-center justify-center text-[#8A91A5]",
              node.children.length === 0 && "invisible",
            )}>
            {isOpen ? (
              <LuChevronDown size={13} />
            ) : (
              <LuChevronRight size={13} />
            )}
          </button>
          <button
            type={"button"}
            onClick={() => openFolder(node.id)}
            className={"flex min-w-0 flex-1 items-center gap-2 text-left"}>
            <FolderGlyph size={16} className={"text-[#6B4EFF]"} />
            <span className={"truncate"}>{node.name}</span>
          </button>
          {node.visibility !== "inherit" && node.effective !== "private" && (
            <span
              title={
                node.effective === "uploads"
                  ? "Client can see and add files"
                  : "Client can see"
              }
              className={"flex-none text-[#1F6FD1]"}>
              <LuEye size={13} />
            </span>
          )}
        </div>
        {isOpen && node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  const title =
    view.type === "toSort"
      ? "To sort"
      : view.type === "work"
      ? currentWork?.name || "Work item"
      : currentFolder?.name || data.name;

  return (
    <div className={"font-Manrope text-[#1F2433]"}>
      <div className={"mb-3 flex flex-wrap items-center gap-2"}>
        <div className={"relative w-[260px] max-w-full"}>
          <LuSearch
            size={15}
            className={
              "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
            }
          />
          <input
            id={"client-files-search"}
            className={classNames(INPUT, "pl-9")}
            placeholder={"Search this client’s files"}
            value={filters.search || ""}
            onChange={(event) =>
              setFilters((f) => ({ ...f, search: event.target.value }))
            }
          />
        </div>
        <select
          id={"client-files-added-by"}
          aria-label={"Added by"}
          className={SELECT}
          value={filters.addedBy || ""}
          onChange={(event) =>
            setFilters((f) => ({
              ...f,
              addedBy: event.target.value as FileFilters["addedBy"],
            }))
          }>
          <option value={""}>Added by: anyone</option>
          <option value={"firm"}>Added by: your team</option>
          <option value={"client"}>Added by: the client</option>
        </select>
        <select
          id={"client-files-type"}
          aria-label={"File type"}
          className={SELECT}
          value={filters.type || ""}
          onChange={(event) =>
            setFilters((f) => ({
              ...f,
              type: event.target.value as FileFilters["type"],
            }))
          }>
          <option value={""}>Type: all</option>
          <option value={"pdf"}>PDF</option>
          <option value={"word"}>Word / text</option>
          <option value={"excel"}>Excel / CSV</option>
          <option value={"image"}>Photos</option>
          <option value={"other"}>Other</option>
        </select>
        <select
          id={"client-files-sort"}
          aria-label={"Sort"}
          className={SELECT}
          value={sort}
          onChange={(event) => setSort(event.target.value as FileSort)}>
          <option value={"newest"}>Newest first</option>
          <option value={"oldest"}>Oldest first</option>
          <option value={"name"}>Name A–Z</option>
          <option value={"size"}>Largest first</option>
        </select>
        {filtering && (
          <button
            type={"button"}
            className={GHOST_BUTTON}
            onClick={() => {
              setFilters({});
              setClientView(false);
            }}>
            <LuX size={14} />
            Clear
          </button>
        )}
        <span className={"flex-1"} />
        <button
          type={"button"}
          className={classNames(GHOST_BUTTON, clientView && "bg-[#EFEBFF]")}
          aria-pressed={clientView}
          onClick={() => setClientView((v) => !v)}>
          <LuEye size={15} />
          See what the client sees
        </button>
        <button
          type={"button"}
          className={SECONDARY_BUTTON}
          onClick={() =>
            setDialog({ type: "newFolder", parentId: uploadFolderId })
          }>
          <LuFolderPlus size={15} />
          New folder
        </button>
        <button
          type={"button"}
          className={PRIMARY_BUTTON}
          onClick={() => setDialog({ type: "upload" })}>
          <LuUpload size={15} />
          Upload
        </button>
      </div>

      <div
        className={
          "grid overflow-hidden rounded-2xl border border-[#E6E9EF] bg-white md:grid-cols-[250px_minmax(0,1fr)]"
        }>
        <nav
          aria-label={"Folders"}
          className={
            "border-b border-[#EDEFF3] bg-[#F8F9FC] p-2.5 md:border-b-0 md:border-r"
          }>
          <button
            type={"button"}
            onClick={() => setView({ type: "toSort" })}
            className={classNames(
              "mb-2 flex w-full items-center gap-2 rounded-lg border border-dashed px-2.5 py-2 text-left text-[13px] font-bold",
              view.type === "toSort"
                ? "border-[#6B4EFF] bg-[#EFEBFF] text-[#4B32D6]"
                : "border-[#C9C0FF] bg-white",
            )}>
            <LuInbox size={16} className={"text-[#6B4EFF]"} />
            To sort
            {toSortCount > 0 && (
              <span
                className={
                  "ml-auto rounded-full bg-[#6B4EFF] px-2 text-[11px] text-white"
                }>
                {toSortCount}
              </span>
            )}
          </button>
          <div
            {...dropProps(null, "top")}
            className={classNames(
              "flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px]",
              view.type === "top" && !filtering
                ? "bg-[#EFEBFF] font-bold text-[#4B32D6]"
                : "font-semibold hover:bg-white",
              dropTarget === "top" && "ring-2 ring-[#6B4EFF]",
            )}>
            <button
              type={"button"}
              className={"flex min-w-0 flex-1 items-center gap-2 text-left"}
              onClick={() => {
                setFilters({});
                setClientView(false);
                setView({ type: "top" });
              }}>
              <FolderGlyph size={16} className={"text-[#8A91A5]"} />
              <span className={"truncate"}>All folders</span>
            </button>
          </div>
          <div className={"mt-0.5"}>
            {tree.map((node) => renderNode(node, 0))}
          </div>
          {worksWithFiles.length > 0 && (
            <>
              <div
                className={
                  "px-2 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.06em] text-[#8A91A5]"
                }>
                From work items
              </div>
              {worksWithFiles.map((w) => (
                <button
                  key={w.id}
                  type={"button"}
                  onClick={() => setView({ type: "work", id: w.id })}
                  className={classNames(
                    "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px]",
                    view.type === "work" && view.id === w.id
                      ? "bg-[#EFEBFF] font-bold text-[#4B32D6]"
                      : "font-semibold text-[#4A5168] hover:bg-white",
                  )}>
                  <LuBriefcase
                    size={14}
                    className={"flex-none text-[#8A91A5]"}
                  />
                  <span className={"truncate"}>{w.name}</span>
                  <span className={"ml-auto text-xs text-[#8A91A5]"}>
                    {w.count}
                  </span>
                </button>
              ))}
            </>
          )}
          <div
            className={
              "mt-3 border-t border-[#EDEFF3] px-2 pt-2 text-xs text-[#667085]"
            }>
            Deleted files are in{" "}
            <Link
              to={"/documents/bin"}
              className={"font-semibold text-[#5A3EEB] hover:underline"}>
              Documents › Recycle bin
            </Link>
            .
          </div>
        </nav>

        <section
          className={"min-w-0 p-3.5"}
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes("Files"))
              event.preventDefault();
          }}
          onDrop={(event) => {
            if (
              !event.dataTransfer.types.includes("Files") ||
              event.dataTransfer.getData(DRAG_TYPE)
            )
              return;
            event.preventDefault();
            setDialog({
              type: "upload",
              files: Array.from(event.dataTransfer.files),
            });
          }}>
          <div
            className={
              "mb-2.5 flex flex-wrap items-center gap-1.5 text-[14px] font-bold"
            }>
            {filtering ? (
              <span>
                {clientView
                  ? "What the client sees in their portal"
                  : "Search results"}{" "}
                <span className={"font-semibold text-[#667085]"}>
                  · {shownFiles.length} file{shownFiles.length === 1 ? "" : "s"}
                </span>
              </span>
            ) : (
              <>
                <button
                  type={"button"}
                  className={"text-[#667085] hover:text-[#5A3EEB]"}
                  onClick={() => setView({ type: "top" })}>
                  {data.name}
                </button>
                {view.type === "toSort" && (
                  <>
                    <LuChevronRight size={13} className={"text-[#9AA0B0]"} />
                    <span>To sort</span>
                  </>
                )}
                {view.type === "work" && (
                  <>
                    <LuChevronRight size={13} className={"text-[#9AA0B0]"} />
                    <span>{title}</span>
                    <Link
                      to={`/work/${view.id}`}
                      className={
                        "ml-1 text-xs font-semibold text-[#5A3EEB] hover:underline"
                      }>
                      Open work item
                    </Link>
                  </>
                )}
                {chain.map((f, i) => (
                  <React.Fragment key={f.id}>
                    <LuChevronRight size={13} className={"text-[#9AA0B0]"} />
                    {i === chain.length - 1 ? (
                      <span>{f.name}</span>
                    ) : (
                      <button
                        type={"button"}
                        className={"text-[#667085] hover:text-[#5A3EEB]"}
                        onClick={() => openFolder(f.id)}>
                        {f.name}
                      </button>
                    )}
                  </React.Fragment>
                ))}
                {currentFolder && (
                  <span className={"ml-1.5"}>
                    <VisibilityPill visibility={currentFolder.effective} />
                  </span>
                )}
                {currentFolder && (
                  <button
                    type={"button"}
                    className={classNames(GHOST_BUTTON, "ml-auto h-8")}
                    onClick={() =>
                      setDialog({ type: "editFolder", folder: currentFolder })
                    }>
                    <LuPencil size={13} />
                    Edit folder
                  </button>
                )}
              </>
            )}
          </div>
          {view.type === "toSort" && !filtering && toSortCount > 0 && (
            <p className={"mb-2 text-[13px] text-[#667085]"}>
              Files the client sent through the portal that aren’t in a folder
              yet. Drag them onto a folder, or tick them and use “Move to…”.
            </p>
          )}
          <FileTable
            files={shownFiles}
            folders={shownFolders}
            columns={{
              location:
                filtering || view.type === "work" || view.type === "toSort",
              visibility: true,
              addedBy: true,
              added: true,
              size: true,
              work: view.type !== "work",
            }}
            selected={selected}
            onSelect={setSelected}
            onOpen={openFile}
            onOpenFolder={(folder) => openFolder(folder.id)}
            onLocation={(file) =>
              file.folderId
                ? openFolder(file.folderId)
                : setView({ type: file.fromClient ? "toSort" : "top" })
            }
            menu={fileMenu}
            folderMenu={folderMenu}
            folderCount={(folder) => countInFolder(folders, files, folder.id)}
            draggable={true}
            empty={
              <div
                className={
                  "rounded-xl border-[1.5px] border-dashed border-[#DCE2EA] px-4 py-10 text-center text-[13px] text-[#667085]"
                }>
                {filtering
                  ? "No files match."
                  : view.type === "toSort"
                  ? "Nothing to sort. Files clients send through the portal show up here."
                  : "No files here yet. Drag files here or click Upload."}
              </div>
            }
          />
          {selectedFiles.length > 0 && (
            <div
              className={
                "sticky bottom-3 mt-3 flex flex-wrap items-center gap-1.5 rounded-xl bg-[#1F2433] px-3.5 py-2 text-[13px] font-bold text-white shadow-lg"
              }>
              <span className={"mr-2"}>{selectedFiles.length} selected</span>
              <span className={"flex-1"} />
              <BulkButton
                onClick={() =>
                  setDialog({ type: "move", files: selectedFiles })
                }>
                Move to…
              </BulkButton>
              <BulkButton
                onClick={() =>
                  setDialog({ type: "link", files: selectedFiles })
                }>
                Work item…
              </BulkButton>
              <BulkButton onClick={() => zip(selectedFiles.map(toRef))}>
                Download (.zip)
              </BulkButton>
              <BulkButton onClick={() => toggleShare(selectedFiles, true)}>
                Share
              </BulkButton>
              <BulkButton onClick={() => toggleShare(selectedFiles, false)}>
                Stop sharing
              </BulkButton>
              <BulkButton
                danger={true}
                onClick={() =>
                  setDialog({ type: "delete", files: selectedFiles })
                }>
                Delete
              </BulkButton>
              <button
                type={"button"}
                aria-label={"Clear selection"}
                className={"ml-1 rounded p-1 hover:bg-white/10"}
                onClick={() => setSelected(new Set())}>
                <LuX size={15} />
              </button>
            </div>
          )}
          {notice && (
            <div
              role={"status"}
              className={
                "mt-3 rounded-lg bg-[#E3F6EE] px-3 py-2 text-[13px] font-semibold text-[#0E8A5F]"
              }>
              {notice}
            </div>
          )}
          <ErrorText>{error}</ErrorText>
        </section>
      </div>

      {preview}
      {dialog?.type === "upload" && (
        <UploadFilesModal
          customerId={customerId}
          clientName={data.name}
          folders={folders}
          works={data.works}
          folderId={uploadFolderId}
          workId={view.type === "work" ? view.id : null}
          initialFiles={dialog.files}
          portalActive={portalActive}
          onClose={() => setDialog(null)}
          onDone={(count) =>
            setNotice(`Uploaded ${count} file${count === 1 ? "" : "s"}.`)
          }
        />
      )}
      {dialog?.type === "newFolder" && (
        <FolderFormModal
          customerId={customerId}
          folders={folders}
          parentId={dialog.parentId}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            if (dialog.parentId)
              setOpen((current) =>
                new Set(current).add(dialog.parentId as string),
              );
            setNotice("Folder created.");
            setTimeout(() => openFolder(id), 0);
          }}
        />
      )}
      {dialog?.type === "editFolder" && (
        <FolderFormModal
          customerId={customerId}
          folders={folders}
          folder={dialog.folder}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "move" && (
        <FolderPickerModal
          title={`Move ${
            dialog.files.length === 1
              ? `“${dialog.files[0].name}”`
              : `${dialog.files.length} files`
          }`}
          customerId={customerId}
          folders={folders}
          initial={dialog.files.length === 1 ? dialog.files[0].folderId : null}
          busy={moving}
          error={error}
          onClose={() => setDialog(null)}
          onPick={(folderId) => move(dialog.files.map(toRef), folderId)}
        />
      )}
      {dialog?.type === "moveFolder" && (
        <FolderPickerModal
          title={`Move folder “${dialog.folder.name}”`}
          customerId={customerId}
          folders={folders}
          initial={dialog.folder.parentId}
          disabledIds={folderSubtree(folders, dialog.folder.id)}
          busy={movingFolder}
          error={error}
          onClose={() => setDialog(null)}
          onPick={async (target) => {
            if (!canMoveFolder(folders, dialog.folder.id, target)) return;
            setError("");
            try {
              await updateFolder({
                id: dialog.folder.id,
                parentId: target,
                moveToTop: target === null,
              }).unwrap();
              setDialog(null);
              setNotice("Folder moved.");
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
      {dialog?.type === "link" && (
        <LinkWorkModal
          files={dialog.files}
          works={data.works}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "rename" && (
        <RenameFileModal file={dialog.file} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "delete" && (
        <DeleteFilesModal
          title={
            dialog.files.length === 1
              ? "Delete this file?"
              : `Delete ${dialog.files.length} files?`
          }
          description={`${
            dialog.files.length === 1
              ? `“${dialog.files[0].name}” goes`
              : "They go"
          } to the recycle bin (Documents › Recycle bin). You can restore ${
            dialog.files.length === 1 ? "it" : "them"
          } for 90 days; after that ${
            dialog.files.length === 1 ? "it is" : "they are"
          } erased.`}
          busy={deleting}
          error={error}
          onClose={() => setDialog(null)}
          onConfirm={() => remove(dialog.files)}
        />
      )}
      {dialog?.type === "deleteFolder" && (
        <DeleteFilesModal
          title={`Delete folder “${dialog.folder.name}”?`}
          description={(() => {
            const count = countInFolder(folders, files, dialog.folder.id);
            const subs = folderSubtree(folders, dialog.folder.id).size - 1;
            return `The folder${
              subs ? ` and its ${subs} subfolder${subs === 1 ? "" : "s"}` : ""
            } will be removed${
              count
                ? `, and its ${count} file${count === 1 ? "" : "s"} go${
                    count === 1 ? "es" : ""
                  } to the recycle bin for 90 days`
                : ""
            }.`;
          })()}
          busy={deletingFolder}
          error={error}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            setError("");
            try {
              const result = await deleteFolder(dialog.folder.id).unwrap();
              setDialog(null);
              setNotice(
                result.count
                  ? `Folder deleted; ${result.count} file${
                      result.count === 1 ? "" : "s"
                    } moved to the recycle bin.`
                  : "Folder deleted.",
              );
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
    </div>
  );
};

const BulkButton: FC<{
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ onClick, danger, children }) => (
  <button
    type={"button"}
    onClick={onClick}
    className={classNames(
      "rounded-md px-2.5 py-1 font-semibold hover:bg-white/10",
      danger ? "text-[#FF9EA1]" : "text-white",
    )}>
    {children}
  </button>
);

export default ClientFilesTab;
